import { AfterViewInit, Component, ContentChild, ElementRef, forwardRef, Inject, Input, OnChanges, TemplateRef, ViewChild } from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR, SelectControlValueAccessor } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslationService } from '@platform-frontend/transloco';
import { TypedChanges, WINDOW } from '@platform-frontend/util';
import { asapScheduler } from 'rxjs';

interface TemplateContext<T> {
  $implicit: T;
}

@UntilDestroy()
@Component({
  selector: 'yat-resizing-select',
  templateUrl: './resizing-select.component.html',
  styleUrls: ['./resizing-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ResizingSelectComponent),
      multi: true
    }
  ]
})
export class ResizingSelectComponent<T> implements OnChanges, AfterViewInit, ControlValueAccessor {
  constructor(private translationService: TranslationService, @Inject(WINDOW) private window: Window) {}

  @ContentChild(TemplateRef)
  templateRef!: TemplateRef<TemplateContext<T>>;

  @Input()
  items: T[];

  @Input()
  valueName?: keyof T = undefined as any;

  @ViewChild(SelectControlValueAccessor, { static: true })
  valueAccessor: SelectControlValueAccessor;

  @ViewChild('mainSelect', { static: true })
  mainSelect: ElementRef<HTMLSelectElement>;

  @ViewChild('shadowSelect', { static: true })
  shadowSelect: ElementRef<HTMLSelectElement>;

  @ViewChild('shadowOption', { static: true })
  shadowOption: ElementRef<HTMLSelectElement>;

  width: string;
  control = new FormControl<unknown>(null);

  private onChangeCallback: (val: unknown) => void;
  private onTouchedCallback: () => void;

  ngOnChanges(changes: TypedChanges<ResizingSelectComponent<any>>): void {
    if (changes.items || changes.valueName) {
      asapScheduler.schedule(() => this.updateWidth());
    }
  }

  ngAfterViewInit(): void {
    this.valueAccessor.registerOnChange(val => {
      this.updateWidth();
      this.onChangeCallback(val);
    });

    this.valueAccessor.registerOnTouched(() => {
      this.onTouchedCallback();
    });

    this.translationService
      .executeOnLangChange(() => this.updateWidth())
      .pipe(untilDestroyed(this))
      .subscribe();
  }

  registerOnChange(callback: (val: unknown) => void): void {
    this.onChangeCallback = callback;
  }

  registerOnTouched(callback: () => void): void {
    this.onTouchedCallback = callback;
  }

  writeValue(value: unknown): void {
    this.control.setValue(value);
    this.updateWidth();
  }

  setDisabledState(isDisabled: boolean): void {
    this.valueAccessor.setDisabledState(isDisabled);
  }

  private updateWidth(): void {
    const mainSelectEl = this.mainSelect.nativeElement;
    const shadowSelectEl = this.shadowSelect.nativeElement;
    const shadowOptionEl = this.shadowOption.nativeElement;

    if (mainSelectEl.selectedIndex >= 0) {
      shadowOptionEl.innerHTML = mainSelectEl.options[mainSelectEl.selectedIndex].innerHTML;
    } else {
      shadowOptionEl.innerHTML = '';
    }

    shadowSelectEl.style.display = 'initial';
    this.width = this.window.getComputedStyle(shadowSelectEl).width || '';
    shadowSelectEl.style.display = 'none';
  }
}
